import { ImperceptibleLink } from '@blackbird/ui-base/link';
import { MoreVert as MoreVertIcon, SettingsSharp } from '@mui/icons-material';
import { Card, CardContent, Grid, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as Schema from 'generated/graphql/schema';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import ReactPlayer from 'react-player/lazy';
import { compose } from 'recompose';

import SignalAvatar from '@/components/icons/signal-avatar';
import { WithStyles, withStyles } from '@/hocs/with-styles';
import SensorSettingsDialog from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { CameraType } from './overview-page';

const styles = (theme: Theme) => ({
  card: {
    minHeight: '100%',
    transition: 'box-shadow .25s',
    '&:hover': {
      boxShadow:
        '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12)',
    },
  },
  actions: {
    paddingLeft: theme.spacing(),
    paddingBottom: theme.spacing(),
    display: 'flex',
    justifyContent: 'space-around',
  },
  button: {
    margin: theme.spacing(),
  },
  nodeIcon: {
    height: 25,
    width: 25,
    marginRight: theme.spacing(),
    marginBottom: -theme.spacing(),
  },
  menu: {},
  menuItemStyles: {
    padding: theme.spacing(),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  menuItemIcon: {
    minWidth: theme.spacing(4),
  },
});

interface Properties {
  device: NonNullable<Schema.DevicesSimpleQuery['devices'][0]>;
  camera: CameraType;
  key: any;
}

interface State {
  isHover: boolean;
  settingsDialogOpen: boolean;
  menuAnchorEl: any;
}

class DeviceOverviewVideoCard extends React.Component<Properties & WithTranslation & WithStyles<typeof styles>, State> {
  readonly state: State = {
    isHover: false,
    settingsDialogOpen: false,
    menuAnchorEl: null,
  };

  handleDialogChange = (open: boolean) => {
    this.setState({ settingsDialogOpen: open });
  };

  handleMenuClose = () => {
    this.setState({ menuAnchorEl: null });
  };

  render() {
    const { classes, device, camera, t } = this.props;

    const { settingsDialogOpen, menuAnchorEl } = this.state;
    const openMenu = Boolean(menuAnchorEl);
    const { uuid } = device;
    const { index, name, description } = camera;

    return (
      <Grid item xs={12} sm={12} md={6}>
        <Card className={classes.card} elevation={1}>
          <ImperceptibleLink href={{ pathname: '/sensor', query: { peripheralId: `${uuid}-${index}`, tab: 'camera' } }}>
            <div style={{ display: 'flex', padding: '16px 24px 16px 24px' }}>
              <div style={{ flex: '1 1 auto' }}>
                <Typography variant="body1">{name}</Typography>
                <Typography color="textSecondary" variant="body2">
                  {description}
                </Typography>
              </div>
              <div
                style={{
                  flex: '0 0 auto',
                  marginLeft: '15px',
                  width: '40px',
                  height: '40px',
                  paddingTop: '3px',
                }}
              >
                {process.env.WIP ? <SignalAvatar /> : null}
              </div>
              <div style={{ flex: '0 0 auto', marginLeft: '5px', width: '40px', height: '40px' }}>
                <IconButton
                  className={classes.menu}
                  aria-label="More"
                  aria-owns={openMenu ? 'long-menu' : undefined}
                  aria-haspopup="true"
                  onClick={this.handleMenuClick}
                  size="large"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="long-menu"
                  anchorEl={menuAnchorEl}
                  open={openMenu}
                  onClose={this.closeMenu}
                  PaperProps={{
                    style: {},
                  }}
                >
                  <MenuItem
                    key={'sensorSettings'}
                    selected={false}
                    onClick={this.openSettingsDialog}
                    className={classes.menuItemStyles}
                  >
                    <ListItemIcon className={classes.menuItemIcon}>
                      <SettingsSharp />
                    </ListItemIcon>
                    <Typography variant="inherit" noWrap>
                      {t(['sensor:sensorSettings'], { defaultValue: 'Sensor settings' })}
                    </Typography>
                  </MenuItem>
                </Menu>
              </div>
            </div>
            <CardContent style={{ padding: '0px' }}>
              <div
                style={{
                  height: '215px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {camera.streamURL !== null ? (
                  <ReactPlayer
                    url={camera.streamURL}
                    width={'100%'}
                    height={'auto'}
                    controls={false}
                    onContextMenu={this.preventContextMenu}
                    config={{
                      file: {
                        hlsOptions: {
                          autoStartLoad: true,
                          maxMaxBufferLength: 0.1,
                        },
                      },
                    }}
                  />
                ) : (
                  <Typography variant="caption" display="block">
                    {t(['device:cameraIsOffline'], { defaultValue: 'Camera appears to be offline' })}
                  </Typography>
                )}
              </div>
            </CardContent>
          </ImperceptibleLink>
        </Card>
        <SensorSettingsDialog
          handleDialogChange={this.handleDialogChange}
          dialogOpen={settingsDialogOpen}
          peripheralId={camera.peripheralId}
        />
      </Grid>
    );
  }
  private preventContextMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    return e.preventDefault();
  };

  private handleMenuClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ menuAnchorEl: e.currentTarget });
  };

  private closeMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    this.handleMenuClose();
  };

  private openSettingsDialog = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      menuAnchorEl: null,
      settingsDialogOpen: true,
    });
  };
}
const enhance = compose<unknown, Properties>(
  withTranslation(['shared', 'device']),
  withStyles(styles, { withTheme: true }),
);

export default enhance(DeviceOverviewVideoCard as React.ComponentType<unknown>);
